/**
 * MusicLibraryModal — Cloudflare-native version
 * All tRPC calls replaced with plain fetch() against Worker REST API.
 *
 * Endpoints used:
 *   GET  /api/director/music?mood=&search=&page=&pageSize=
 *   GET  /api/director/music/:trackId/presign
 *   POST /api/director/music/scan
 */
import { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Music, Search, Play, Pause, RefreshCw, Check, X, Loader2,
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────
const MOOD_LABELS: Record<string, string> = {
  somber:      "Somber",
  reflective:  "Reflective",
  peaceful:    "Peaceful",
  uplifting:   "Uplifting",
  celebratory: "Celebratory",
  upbeat:      "Upbeat",
  nostalgic:   "Nostalgic",
  spiritual:   "Spiritual",
  custom:      "Custom / Other",
};
const MOOD_COLORS: Record<string, string> = {
  somber:      "bg-slate-700/60 text-slate-300 border-slate-600",
  reflective:  "bg-blue-900/40 text-blue-300 border-blue-700",
  peaceful:    "bg-teal-900/40 text-teal-300 border-teal-700",
  uplifting:   "bg-amber-900/40 text-amber-300 border-amber-700",
  celebratory: "bg-rose-900/40 text-rose-300 border-rose-700",
  upbeat:      "bg-emerald-900/40 text-emerald-300 border-emerald-700",
  nostalgic:   "bg-purple-900/40 text-purple-300 border-purple-700",
  spiritual:   "bg-indigo-900/40 text-indigo-300 border-indigo-700",
  custom:      "bg-zinc-800/60 text-zinc-400 border-zinc-600",
};
type Track = {
  id: number;
  r2Key: string;
  filename: string;
  title: string | null;
  artist: string | null;
  mood: string;
  durationSec: number | null;
};
interface MusicLibraryModalProps {
  onSelect: (track: { title: string; r2Key: string }) => void;
  onClose: () => void;
  currentSelection?: string;
}

// ─── API helper ───────────────────────────────────────────────────────────────
async function apiFetch<T = unknown>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(options.headers ?? {}) },
    ...options,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(body.error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

// ─── Duration formatter ───────────────────────────────────────────────────────
function fmtDuration(sec: number | null): string {
  if (!sec) return "";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

// ─── Mini audio preview player ────────────────────────────────────────────────
function TrackPreviewPlayer({ trackId }: { trackId: number }) {
  const [playing, setPlaying]   = useState(false);
  const [url, setUrl]           = useState<string | null>(null);
  const [loading, setLoading]   = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const toggle = useCallback(async () => {
    if (!playing) {
      // First press: fetch presigned URL then start playing
      if (!url) {
        setLoading(true);
        try {
          const d = await apiFetch<{ url: string }>(`/director/music/${trackId}/presign`);
          setUrl(d.url);
        } catch {
          toast.error("Could not load preview");
          return;
        } finally {
          setLoading(false);
        }
      }
      setPlaying(true);
      return;
    }
    // Subsequent presses: toggle pause/play
    if (audioRef.current) {
      audioRef.current.paused ? audioRef.current.play() : audioRef.current.pause();
    }
  }, [playing, url, trackId]);

  return (
    <div className="flex items-center gap-1.5">
      {url && (
        <audio
          ref={audioRef}
          src={url}
          autoPlay
          onEnded={() => setPlaying(false)}
          className="hidden"
        />
      )}
      <button
        type="button"
        onClick={toggle}
        disabled={loading}
        className="w-7 h-7 rounded-full bg-zinc-700 hover:bg-amber-700 flex items-center justify-center transition-colors disabled:opacity-40"
        title={playing ? "Pause preview" : "Play preview"}
      >
        {loading ? (
          <Loader2 className="w-3 h-3 animate-spin text-zinc-400" />
        ) : playing ? (
          <Pause className="w-3 h-3 text-white" />
        ) : (
          <Play className="w-3 h-3 text-white ml-0.5" />
        )}
      </button>
    </div>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────
export function MusicLibraryModal({ onSelect, onClose, currentSelection }: MusicLibraryModalProps) {
  const [activeMood, setActiveMood] = useState<string>("all");
  const [search, setSearch]         = useState("");
  const [tracks, setTracks]         = useState<Track[]>([]);
  const [total, setTotal]           = useState(0);
  const [isLoading, setIsLoading]   = useState(true);
  const [scanning, setScanning]     = useState(false);

  const fetchTracks = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ page: "1", pageSize: "100" });
      if (activeMood !== "all") params.set("mood", activeMood);
      if (search.trim()) params.set("search", search.trim());
      const d = await apiFetch<{ tracks: Track[]; total: number }>(`/director/music?${params}`);
      setTracks(d.tracks);
      setTotal(d.total);
    } catch (e) {
      toast.error((e as Error).message || "Failed to load music library");
    } finally {
      setIsLoading(false);
    }
  }, [activeMood, search]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => fetchTracks(), 300);
    return () => clearTimeout(t);
  }, [fetchTracks]);

  const handleScan = async () => {
    setScanning(true);
    try {
      const result = await apiFetch<{ added: number; updated: number }>("/director/music/scan", { method: "POST" });
      toast.success(`Library scanned — ${result.added} new, ${result.updated} updated.`);
      fetchTracks();
    } catch (e) {
      toast.error((e as Error).message || "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative w-full max-w-2xl max-h-[85vh] flex flex-col bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-zinc-800">
          <Music className="w-4 h-4 text-amber-400 shrink-0" />
          <h2 className="font-['Playfair_Display'] text-base font-semibold text-white flex-1">
            Music Library
          </h2>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleScan}
            disabled={scanning}
            className="h-7 px-3 text-xs text-zinc-400 hover:text-white gap-1.5"
          >
            <RefreshCw className={`w-3 h-3 ${scanning ? "animate-spin" : ""}`} />
            {scanning ? "Scanning…" : "Scan Library"}
          </Button>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Filters */}
        <div className="px-5 pt-3 pb-2 space-y-2 border-b border-zinc-800">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500 pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setActiveMood("all"); }}
              placeholder="Search by title, artist, or filename…"
              className="pl-8 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-600 text-sm h-8"
            />
          </div>
          <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
            {["all", ...Object.keys(MOOD_LABELS)].map((mood) => (
              <button
                key={mood}
                type="button"
                onClick={() => { setActiveMood(mood); setSearch(""); }}
                className={`shrink-0 px-3 py-1 rounded-full text-[11px] font-medium border transition-all ${
                  activeMood === mood
                    ? "bg-amber-700 text-white border-amber-600"
                    : "bg-zinc-800 text-zinc-400 border-zinc-700 hover:border-zinc-500"
                }`}
              >
                {mood === "all" ? "All Tracks" : MOOD_LABELS[mood]}
              </button>
            ))}
          </div>
        </div>

        {/* Track list */}
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-1.5">
          {isLoading && (
            <div className="flex items-center justify-center py-12 text-zinc-600">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Loading library…
            </div>
          )}
          {!isLoading && tracks.length === 0 && (
            <div className="text-center py-12 text-zinc-600">
              <Music className="w-8 h-8 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No tracks found.</p>
              <p className="text-xs mt-1">
                Upload MP3s to the <code className="text-zinc-500">sovereign-music/</code> R2 bucket
                then click <strong className="text-zinc-400">Scan Library</strong>.
              </p>
            </div>
          )}
          {tracks.map((track) => {
            const isSelected = currentSelection === (track.title ?? track.filename);
            return (
              <div
                key={track.id}
                className={`flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors group ${
                  isSelected
                    ? "bg-amber-900/20 border border-amber-700/40"
                    : "bg-zinc-800/50 hover:bg-zinc-800 border border-transparent"
                }`}
              >
                <TrackPreviewPlayer trackId={track.id} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{track.title ?? track.filename}</p>
                  {track.artist && <p className="text-xs text-zinc-500 truncate">{track.artist}</p>}
                </div>
                <span className={`shrink-0 text-[10px] px-2 py-0.5 rounded-full border font-medium ${MOOD_COLORS[track.mood] ?? MOOD_COLORS.custom}`}>
                  {MOOD_LABELS[track.mood] ?? track.mood}
                </span>
                {track.durationSec && (
                  <span className="shrink-0 text-[10px] text-zinc-600 tabular-nums w-8 text-right">
                    {fmtDuration(track.durationSec)}
                  </span>
                )}
                <Button
                  size="sm"
                  onClick={() => { onSelect({ title: track.title ?? track.filename, r2Key: track.r2Key }); onClose(); }}
                  className={`shrink-0 h-7 px-3 text-xs ${isSelected ? "bg-amber-700 hover:bg-amber-600 text-white" : "bg-zinc-700 hover:bg-amber-700 text-zinc-300 hover:text-white"}`}
                >
                  {isSelected ? <><Check className="w-3 h-3 mr-1" />Selected</> : "Select"}
                </Button>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-zinc-800 flex items-center justify-between">
          <p className="text-[10px] text-zinc-600">
            {total > 0
              ? `${tracks.length} of ${total} track${total !== 1 ? "s" : ""}`
              : tracks.length > 0
              ? `${tracks.length} track${tracks.length !== 1 ? "s" : ""} shown`
              : "Library empty — scan to populate"}
          </p>
          <Button size="sm" variant="ghost" onClick={onClose} className="text-zinc-500 hover:text-white h-7 px-3 text-xs">
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
